import { Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { formatName } from "../../utils/generalUtils";

import ArialRegular from "../../generatePdf/components/Arial/ARIAL.TTF";
import ArialBold from "../../generatePdf/components/Arial/ARIALBD.TTF";
import ArialItalic from "../../generatePdf/components/Arial/ARIALI.TTF";

Font.register({ family: "Arial", format: "truetype", src: ArialRegular });
Font.register({ family: "ArialBold", format: "truetype", src: ArialBold });
Font.register({ family: "ArialItalic", format: "truetype", src: ArialItalic });

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function ordinalSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatPeriod(startDate, endDate) {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const startMonth = MONTH_NAMES[start.getMonth()];
  const endMonth = MONTH_NAMES[end.getMonth()];
  const year = end.getFullYear();
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    if (start.getDate() === end.getDate()) {
      return `${startMonth} ${start.getDate()}, ${year}`;
    }
    return `${startMonth} ${start.getDate()}-${end.getDate()}, ${year}`;
  }
  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${year}`;
}

function formatSubmittedDateTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatIssuedDate(dateStr) {
  if (!dateStr) return { day: "__", monthYear: "____________" };
  const clean = dateStr.slice(0, 10);
  const d = new Date(clean + "T00:00:00");
  return {
    day: ordinalSuffix(d.getDate()),
    monthYear: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
  };
}

function formatAbsentDates(absentDates) {
  if (!Array.isArray(absentDates) || absentDates.length === 0) return "";
  // New submissions store a single free-text string; old ones store ISO date strings.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(absentDates[0])) {
    return absentDates[0] || "";
  }
  return absentDates
    .map((ds) => {
      const d = new Date(ds + "T00:00:00");
      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    })
    .join(", ");
}

const styles = StyleSheet.create({
  container: {
    fontFamily: "Arial",
    fontSize: 11,
    paddingHorizontal: 60,
    paddingVertical: 30,
  },
  centered: {
    textAlign: "center",
  },
  bold: {
    fontFamily: "ArialBold",
  },
  italic: {
    fontFamily: "ArialItalic",
  },
  sectionTitle: {
    fontFamily: "ArialBold",
    fontSize: 12,
    textAlign: "center",
    marginBottom: 14,
  },
  bodyText: {
    fontSize: 11,
    textAlign: "justify",
    lineHeight: 1.5,
    textIndent: 36,
    marginBottom: 8,
  },
  bulletRow: {
    flexDirection: "row",
    paddingLeft: 52,
    marginBottom: 4,
  },
  bulletMark: {
    width: 14,
    fontSize: 11,
  },
  bulletText: {
    flex: 1,
    fontSize: 11,
    textAlign: "justify",
    lineHeight: 1.4,
  },
  underline: {
    textDecoration: "underline",
  },
  rightBlock: {
    alignItems: "center",
    marginTop: 24,
    marginLeft: "auto",
    width: "45%",
  },
  signatureLine: {
    borderBottom: "1px solid #000",
    width: "100%",
    marginBottom: 3,
  },
  approvalContainer: {
    fontSize: 10,
    gap: 0,
  },
  approvalFields: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  approvalFieldName: {
    fontSize: 8,
    fontFamily: "ArialItalic",
    border: "1px solid black",
    textAlign: "center",
    padding: 2,
  },
});

function CwaFormat({ contents, isEmployee }) {
  const requesterName = contents.formattedName
    ? contents.formattedName
    : contents.name;

  const jobTitle = contents?.submitter_position ?? "";

  const submitterEmail = contents?.submitter_email
    ?? contents?.submission?.submitter?.email
    ?? null;

  const approverName = isEmployee
    ? formatName(
        contents?.submission?.approval?.[0]?.approver?.first_name,
        contents?.submission?.approval?.[0]?.approver?.middle_name,
        contents?.submission?.approval?.[0]?.approver?.last_name,
        "lfmcut"
      )
    : contents?.formattedApproverName;

  const approvalDate = contents?.approval_date ?? "";

  const startDate = contents?.start_date ?? "";
  const endDate = contents?.end_date ?? "";
  const period = formatPeriod(startDate, endDate);

  const absentDates = Array.isArray(contents?.absent_dates)
    ? contents.absent_dates
    : [];
  const absentText = formatAbsentDates(absentDates);

  const accomplishments = Array.isArray(contents?.accomplishments)
    ? contents.accomplishments
    : [];

  const submittedDateTime = formatSubmittedDateTime(contents?.created_at);
  const remarks = contents?.remarks || null;

  const issuedRaw =
    contents?.status === "approved"
      ? contents?.approval_date
      : contents?.date_submitted;
  const { day: issuedDay, monthYear: issuedMonthYear } = formatIssuedDate(issuedRaw);

  return (
    <View style={styles.container}>
      {/* ── HEADER ── */}
      <View style={{ marginBottom: 8 }}>
        <Text style={[styles.centered, { fontSize: 12 }]}>University of the Philippines</Text>
        <Text style={[styles.centered, { fontSize: 12 }]}>Open University</Text>
        <Text style={[styles.centered, { fontSize: 11 }]}>Los Baños, Laguna</Text>
      </View>

      {/* ── TITLE ── */}
      <Text style={[styles.sectionTitle, { marginTop: 12, marginBottom: 18 }]}>
        CERTIFICATE OF WORK ACCOMPLISHMENT
      </Text>

      {/* ── BODY PARAGRAPH ── */}
      <Text style={styles.bodyText}>
        {"This is to certify that I have performed the following tasks (as stipulated in my contract of service for COS staff) as "}
        <Text style={[styles.bold, styles.underline]}>{jobTitle}</Text>
        {" for the period of "}
        <Text style={[styles.bold, styles.underline]}>{period}</Text>
        {absentText
          ? <Text>{", except on the following days: "}<Text style={styles.underline}>{absentText}</Text>{":"}</Text>
          : ":"}
      </Text>

      {/* ── ACCOMPLISHMENTS ── */}
      <View style={{ marginTop: 4, marginBottom: 12 }}>
        {accomplishments.map((item, idx) => (
          <View key={idx} style={styles.bulletRow}>
            <Text style={styles.bulletMark}>●</Text>
            <Text style={styles.bulletText}>{item}</Text>
          </View>
        ))}
      </View>

      {/* ── ISSUANCE LINE ── */}
      <Text style={styles.bodyText}>
        {"Issued this "}
        <Text style={[styles.bold, styles.underline]}>{issuedDay}</Text>
        {" day of "}
        <Text style={[styles.bold, styles.underline]}>{issuedMonthYear}</Text>
        {" at the UP Open University, Los Baños, Laguna."}
      </Text>

      {/* ── REMARKS + SIGNATURE ROW ── */}
      <View style={{ flexDirection: "row", alignItems: "flex-start", marginTop: 24 }}>
        {/* Left: Remarks */}
        <View style={{ flex: 1, paddingRight: 32 }}>
          {remarks ? (
            <>
              <Text style={{ fontSize: 9, fontFamily: "ArialBold", marginBottom: 2 }}>Remarks:</Text>
              <Text style={{ fontSize: 9, fontFamily: "Arial", lineHeight: 1.4, textAlign: "justify" }}>{remarks}</Text>
            </>
          ) : null}
        </View>

        {/* Right: Signature block */}
        <View style={{ alignItems: "center", width: "45%" }}>
          {submitterEmail ? (
            <Text style={{ fontSize: 9, fontFamily: "ArialItalic", color: "#555555", marginBottom: 4, textAlign: "center" }}>
              {submitterEmail}
            </Text>
          ) : null}
          <Text style={{ fontSize: 11, textAlign: "center", marginBottom: 6 }}>
            {"Date/Time Submitted: "}{submittedDateTime}
          </Text>
          <View style={styles.signatureLine} />
          <Text style={[styles.bold, { textTransform: "uppercase", textAlign: "center", fontSize: 11, marginTop: 3 }]}>
            {requesterName}
          </Text>
        </View>
      </View>

      {/* ── CERTIFICATE OF ACCEPTANCE ── */}
      <Text style={[styles.sectionTitle, { marginTop: 28 }]}>CERTIFICATE OF ACCEPTANCE</Text>

      <Text style={styles.bodyText}>
        This is to certify that the works accomplished as stated above are hereby accepted by the undersigned (and in consonance to the stipulation of the contract of service entered by the parties hereto attached for COS staff).
      </Text>

      {/* ── ACCEPTED BY ── */}
      <Text style={[styles.centered, { marginTop: 14, marginBottom: 4, fontSize: 11 }]}>
        ACCEPTED BY:
      </Text>

      {/* ── APPROVAL HISTORY TABLE ── */}
      <View style={[styles.approvalContainer, { marginTop: 10 }]}>
        <Text style={{ fontSize: 10, marginBottom: 4 }}>Approval History</Text>
        <View style={styles.approvalFields}>
          <Text style={{ ...styles.approvalFieldName, width: "40%" }}>Name</Text>
          <Text style={{ ...styles.approvalFieldName, borderLeft: "none", borderRight: "none", width: "30%" }}>Action</Text>
          <Text style={{ ...styles.approvalFieldName, width: "30%" }}>Date/Time</Text>
        </View>
        <View style={styles.approvalFields}>
          <Text style={{ ...styles.approvalFieldName, borderTop: "none", textTransform: "uppercase", width: "40%" }}>
            {approverName}
          </Text>
          <Text style={{ ...styles.approvalFieldName, borderTop: "none", borderLeft: "none", borderRight: "none", textTransform: "uppercase", width: "30%" }}>
            approved
          </Text>
          <Text style={{ ...styles.approvalFieldName, borderTop: "none", textTransform: "uppercase", width: "30%" }}>
            {approvalDate}
          </Text>
        </View>
      </View>

      {/* ── ENDNOTE ── */}
      <View style={{ fontFamily: "ArialItalic", fontSize: 8, marginTop: 16 }}>
        <Text>This is a UPOU-PIVOT generated report.</Text>
        <Text>Approver's signature not required</Text>
      </View>
    </View>
  );
}

export default CwaFormat;
